import { InvaderKind, InvaderSeed, BunkerSeed } from "./types";

export interface GameConfig {
  canvas: { width: number; height: number };
  player: {
    width: number;
    height: number;
    speed: number;
    y: number;
    fireCooldown: number;
    startLives: number;
    spriteScale: number;
  };
  bullet: {
    width: number;
    height: number;
    playerSpeed: number;
    invaderSpeed: number;
    maxInvaderBullets: number;
    maxPlayerBullets: number;
  };
  swarm: {
    rows: number;
    cols: number;
    invaderWidth: number;
    invaderHeight: number;
    invaderScale: number;
    hGap: number;
    vGap: number;
    marginTop: number;
    stepDown: number;
    baseSpeed: number;
    maxSpeed: number;
    animInterval: number;
    fireInterval: number;
    fireIntervalStep: number;
    minFireInterval: number;
    levelDrop: number;
  };
  bunker: {
    count: number;
    rows: number;
    cols: number;
    blockSize: number;
    y: number;
    shape: number[][];
  };
  points: Record<InvaderKind, number>;
  game: { maxLevels: number };
  colors: {
    background: string;
    player: string;
    invaderTop: string;
    invaderMid: string;
    invaderBottom: string;
    playerBullet: string;
    invaderBullet: string;
    bunker: string;
    text: string;
    overlay: string;
  };
  /** Draw the SCORE/LEVEL/LIVES row. */
  hud: boolean;
  /** Draw the dimmed title card in every non-Playing state. */
  overlay: boolean;
  /** Seconds to wait before restarting on Win/GameOver. `null` waits for input. */
  autoRestartDelay: number | null;
  /**
   * Injected for determinism in tests. Read through `?? Math.random` at the
   * call site, never captured, so `jest.spyOn(Math, "random")` still works.
   */
  random?: () => number;
  /** Entity placement. Absent members fall back to the classic grid layout. */
  layout?: {
    swarm?: (level: number, cfg: GameConfig) => InvaderSeed[];
    bunkers?: (cfg: GameConfig) => BunkerSeed[];
  };
}

export const CONFIG: GameConfig = {
  canvas: { width: 700, height: 760 },

  player: {
    width: 39,
    height: 24,
    speed: 320, // px/sec
    y: 710, // top edge of the player ship
    fireCooldown: 0.4, // seconds between shots
    startLives: 3,
    spriteScale: 3,
  },

  bullet: {
    width: 4,
    height: 14,
    playerSpeed: 520, // px/sec, travels up
    invaderSpeed: 240, // px/sec, travels down
    maxInvaderBullets: 3,
    maxPlayerBullets: 1, // the classic one-shot-on-screen rule
  },

  swarm: {
    rows: 5,
    cols: 11,
    invaderWidth: 30,
    invaderHeight: 22,
    invaderScale: 2,
    hGap: 14,
    vGap: 16,
    marginTop: 90,
    stepDown: 20, // px dropped when an edge is hit
    baseSpeed: 26, // px/sec with a full fleet
    maxSpeed: 240, // px/sec when nearly cleared
    animInterval: 0.5, // seconds between animation frames
    fireInterval: 1.0, // seconds between swarm shots at level 1
    fireIntervalStep: 0.12, // reduction per level
    minFireInterval: 0.35,
    levelDrop: 24, // extra starting y per level
  },

  bunker: {
    count: 4,
    rows: 8,
    cols: 12,
    blockSize: 6,
    y: 610,
    // 1 = solid block, 0 = empty. Classic silhouette with a bottom arch.
    shape: [
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 1],
      [1, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 1],
    ] as number[][],
  },

  points: { top: 30, mid: 20, bottom: 10 } as Record<InvaderKind, number>,

  game: { maxLevels: 5 },

  colors: {
    background: "#000000",
    player: "#33ff66",
    invaderTop: "#ff5fa2",
    invaderMid: "#7df9ff",
    invaderBottom: "#ffe066",
    playerBullet: "#ffffff",
    invaderBullet: "#ff6464",
    bunker: "#33ff66",
    text: "#ffffff",
    overlay: "rgba(0, 0, 0, 0.6)",
  },

  hud: true,
  overlay: true,
  autoRestartDelay: null,
};
